import * as React from "react";
import { useFocusRing } from "@react-aria/focus";
import { useTextField } from "@react-aria/textfield";
import { mergeProps } from "@react-aria/utils";
import { css } from "@nju/css";
import { Box, Text } from "@nju/ui";

export function TextArea(props: {
  "aria-label"?: string;
  name: string;
  placeholder?: string;
  label?: string;
  value?: string;
  defaultValue?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  rows?: number;
  cols?: number;
  wrap?: string;
  maxLength?: number;
  textLength?: number;
  onChange?: (value: string) => void;
}) {
  const {
    label,
    cols,
    rows,
    wrap,
    onChange,
    isDisabled,
    placeholder,
    defaultValue,
  } = props;
  const [textLength, setTextLength] = React.useState(defaultValue?.length || 0);

  const handleOnChange = React.useCallback(
    (value: string) => {
      if (onChange) {
        onChange(value);
      }
      setTextLength(value.length);
    },
    [setTextLength, onChange],
  );

  const ref = React.useRef(null);
  const { labelProps, inputProps } = useTextField(
    { ...props, defaultValue, onChange: handleOnChange },
    ref,
  );

  const { focusProps, isFocused } = useFocusRing({
    isTextInput: true,
  });

  return (
    <Box className={styles.wrapper()}>
      {label && (
        <label
          {...labelProps}
          className={styles.label({
            offside: isFocused || !!textLength,
          })}
        >
          {placeholder
            ? (isFocused && label) || (textLength > 0 && label)
            : label}
        </label>
      )}

      <textarea
        {...mergeProps(inputProps, focusProps)}
        className={styles.textarea({
          focus: isFocused,
          disabled: isDisabled,
        })}
        cols={cols}
        ref={ref}
        rows={rows}
        wrap={wrap}
      />
      {inputProps.maxLength && (
        <Text align="right" size="s">
          {textLength}/{inputProps.maxLength}
        </Text>
      )}
    </Box>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    mt: "$20",
  }),
  label: css({
    position: "absolute",
    top: "15%",
    left: 0,
    color: "$geyser",
    borderRadius: "$4",
    fontSize: "$16",
    backgroundColor: "transparent",
    ml: "$30",
    transform: `translateY(-50%)`,
    transition:
      "$color ease-in, font-size 0.15s ease-in, $bgColor ease-in, $transform ease-in",
    pointerEvents: "none",
    "@fromTablet": {
      fontSize: "$20",
    },
    variants: {
      offside: {
        true: {
          padding: "0px 8px",
          color: "$daintree",
          fontSize: "$14",
          backgroundColor: "$white",
          transform: `translateY(-210%)`,
        },
      },
    },
  }),
  textarea: css({
    resize: "none",
    border: "2px solid $geyser",
    borderRadius: 27.5,
    px: "$25",
    py: "$20",
    fontSize: "$16",
    mb: "$10",
    "@fromTablet": { fontSize: "$20" },
    "&::placeholder": {
      color: "$geyser",
      opacity: 1,
    },
    variants: {
      focus: {
        true: {
          borderColor: "$ripeLemon",
          boxShadow: "$basic $colors$ripeLemon",
        },
      },
      disabled: {
        true: {
          borderColor: "$geyser",
          backgroundColor: "$porcelain",
        },
      },
    },
  }),
};
